import { useEffect } from "react";
import { fetchInitialLocations, getVehicleDirection, getVehiclesByOrgIdIndependent } from "./mobileApis";

export const BUS_REFRESH_INTERVAL = 30000;

// Refresh independently of SSE so direction changes and quiet GPS streams
// still cause every supplied vehicle to be reconsidered.
export default function useBusRefresh(deviceIds, setLiveData, setDirections, organizationId, setStatuses) {
  useEffect(() => {
    if (!deviceIds.length) return;
    let cancelled = false;
    let refreshing = false;
    const refresh = async () => {
      if (refreshing) return;
      refreshing = true;
      const startedAt = Date.now();
      try {
        await Promise.allSettled([
          organizationId && setStatuses
            ? getVehiclesByOrgIdIndependent(organizationId).then(vehicles => {
                if (cancelled) return;
                const statuses = {};
                for (const vehicle of vehicles || []) {
                  const id = vehicle.deviceId || vehicle.device_id || vehicle.device?.id || vehicle.id;
                  if (id) statuses[id] = vehicle.status;
                }
                setStatuses(statuses);
              }).catch(() => { if (!cancelled) setStatuses({}); })
            : Promise.resolve(),
          fetchInitialLocations(deviceIds).then(locations => {
            if (cancelled) return;
            setLiveData(previous => {
              const next = { ...previous };
              Object.entries(locations || {}).forEach(([id, location]) => {
                // A streaming update received during this request is fresher.
                if (Date.parse(previous[id]?.receivedAt) >= startedAt) return;
                next[id] = location;
              });
              return next;
            });
          }),
          Promise.all(deviceIds.map(async id => {
            try { return [id, await getVehicleDirection(id)]; }
            catch { return [id, "unknown"]; }
          })).then(entries => {
            if (!cancelled) setDirections(Object.fromEntries(entries));
          }),
        ]);
      } finally {
        refreshing = false;
      }
    };
    refresh();
    const interval = setInterval(refresh, BUS_REFRESH_INTERVAL);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [deviceIds, setLiveData, setDirections, organizationId, setStatuses]);
}
